import { Combobox, Listbox } from '@headlessui/react'
import { RefObject, useEffect, useMemo, useRef, useState } from 'react'
import { Option, OptionGroup } from '../option'
import { SelectOption, SelectOptionGroup } from '../option.types'

export const useMultiSelect = (data: {
  options: (SelectOption | SelectOptionGroup)[]
  searchable?: boolean
  placeholder?: string
  defaultValue?: Array<SelectOption>
  noResultsFoundText?: string
  allowClear?: boolean
}) => {
  const {
    options,
    searchable,
    placeholder,
    defaultValue = [],
    noResultsFoundText = 'No results found',
    allowClear,
  } = data

  const [selectedOptions, setSelectedOptions] =
    useState<SelectOption[]>(defaultValue)

  const [searchQuery, setSearchQuery] = useState('')

  const inputRef = useRef<HTMLInputElement>() as RefObject<HTMLInputElement>

  useEffect(() => {
    if (defaultValue.length > 0 || allowClear) {
      setSelectedOptions(defaultValue)
    }
  }, [allowClear, defaultValue])

  const renderElements = useMemo(() => {
    if (!options.length) {
      return <div style={{ padding: '1rem' }}>{noResultsFoundText}</div>
    }

    const optionMatchesSearchQuery = (option: SelectOption) => {
      if (!searchQuery || !searchable) {
        return true
      }

      const searchableText = [option.label, option.description, option.value]
        .join(' ')
        .toLowerCase()

      return searchableText.includes(searchQuery.toLowerCase())
    }

    const ListType = searchable ? Combobox : Listbox

    const renderOption = (option: SelectOption) => {
      return (
        <ListType.Option
          key={option.value}
          value={option}
          style={{
            listStyle: 'none',
            marginLeft: 0,
            paddingLeft: 0,
            border: 'none',
          }}
        >
          {({ active }) => (
            <Option
              {...option}
              isActive={active}
              multiSelect
              isSelected={selectedOptions.includes(option)}
            />
          )}
        </ListType.Option>
      )
    }

    const resultsMap = options
      .map((opt) => {
        if (Object.keys(opt).includes('title')) {
          const subOptionCollection = (opt as SelectOptionGroup).options.filter(
            optionMatchesSearchQuery
          )

          if (subOptionCollection.length === 0) return null

          const optionTitle = (opt as SelectOptionGroup).title

          return (
            <OptionGroup key={optionTitle} title={optionTitle}>
              {subOptionCollection.map((subOpt) => {
                return renderOption(subOpt)
              })}
            </OptionGroup>
          )
        } else {
          const castedOption = opt as SelectOption

          return optionMatchesSearchQuery(castedOption)
            ? renderOption(castedOption)
            : null
        }
      })
      .filter((result) => result !== null)

    if (resultsMap.length === 0) {
      return <div style={{ padding: '1rem' }}>{noResultsFoundText}</div>
    }

    return resultsMap
  }, [options, noResultsFoundText, selectedOptions, searchable, searchQuery])

  const inputProps = {
    ref: inputRef,
    placeholder: placeholder,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
      setSearchQuery(e.target.value),
  }

  const triggerProps = {
    onClick: () => {
      inputRef.current?.focus()
    },
  }

  return {
    triggerProps,
    value: selectedOptions,
    inputProps,
    renderElements,
    setValue: (value: SelectOption[]) => {
      setSelectedOptions(value)
    },
    searchQuery,
    afterLeave: () => setSearchQuery(''),
  }
}
